import StockAndPriceData from '../model/stock-and-price-data';
import WarehouseStockData from '../model/warehouse-stock-data';
import StockAvailability from '../components/shared/stock-availability';
import DeliveryAvailability from '../components/product/delivery-availability';
import AvailabilityStatusIcon from '../components/product/availability-status-icon';
import OrderDeadline from '../components/product/order-deadline';
import MessageHelper from './message-helper';
import ProductFlags from '../model/product-flags';
import { RowReader } from './row-reader';

type InstanceElement<T> = HTMLElement & { instance?: T };

function resolveWarehouses(
  data: StockAndPriceData,
  flags: ProductFlags,
): WarehouseStockData[] | null {
  if (flags.isMasterClass) {
    const hasKnownForecast = data.stockForecasts?.some((f) => !f.unknownForecast);
    return hasKnownForecast ? null : [];
  }
  return data.warehouses?.filter((w) => w.type !== 'CUSTOMER') ?? [];
}

function renderStockAvailability(element: HTMLElement, data: StockAndPriceData, flags: ProductFlags): void {
  const el = element as InstanceElement<StockAvailability>;
  if (el.instance) {
    el.instance.destroy();
    delete el.instance;
  }

  const warehouses = resolveWarehouses(data, flags);
  if (warehouses === null) return;

  try {
    el.instance = new StockAvailability(el as HTMLDivElement, flags, warehouses);
  } catch (e: unknown) {
    if (e instanceof Error) {
      // eslint-disable-next-line no-console
      console.error('StockAvailability render failed:', e.message);
    }
  }
}

function renderDeliveryAvailability(element: HTMLElement, data: StockAndPriceData, flags: ProductFlags): void {
  const el = element as InstanceElement<DeliveryAvailability>;
  if (el.instance) {
    el.instance.destroyTooltipInstance();
    delete el.instance;
  }
  el.instance = new DeliveryAvailability(el, data.stockForecasts);
}

function renderAvailabilityStatusIcon(element: HTMLElement, data: StockAndPriceData): void {
  const el = element as InstanceElement<AvailabilityStatusIcon>;
  if (el.instance) {
    el.instance.setStockForecast(data.stockForecasts);
  } else {
    el.instance = new AvailabilityStatusIcon(el, data.stockForecasts);
  }
}

function renderOrderDeadline(element: HTMLElement, data: StockAndPriceData): void {
  const el = element as InstanceElement<OrderDeadline>;
  if (el.instance) {
    el.instance.setStockForecast(data.stockForecasts);
  } else {
    el.instance = new OrderDeadline(el, data.stockForecasts);
  }
}

function renderStockCustomerLabel(element: HTMLElement, data: StockAndPriceData): void {
  if (!data.warehouses) return;
  const stockLevel = data.warehouses.find((w) => w.type === 'CUSTOMER')?.stockLevelAmount ?? 0;
  if (stockLevel > 0) {
    $(element).html(`<b>${stockLevel}</b> ${MessageHelper.getMessage('common.customer.stock')}`);
  }
}

function renderWarehouses(element: HTMLElement, data: StockAndPriceData): void {
  if (!data.warehouses) return;
  const stockLevel = data.warehouses.find((w) => w.type === 'CUSTOMER')?.stockLevelAmount ?? 0;
  if (stockLevel > 0) {
    $(element).text(String(stockLevel));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function renderStockFields(row: JQuery, data: StockAndPriceData, flags: ProductFlags): void {
  RowReader.ownDescendants(row, '.js-asyncField').each((_, fieldEl) => {
    const key = String($(fieldEl).data('async-key') ?? '');

    switch (key) {
      case StockAvailability.ASYNC_KEY:
        renderStockAvailability(fieldEl, data, flags);
        break;
      case DeliveryAvailability.ASYNC_KEY:
        renderDeliveryAvailability(fieldEl, data, flags);
        break;
      case AvailabilityStatusIcon.ASYNC_KEY:
        renderAvailabilityStatusIcon(fieldEl, data);
        break;
      case OrderDeadline.ASYNC_KEY:
        renderOrderDeadline(fieldEl, data);
        break;
      case 'stockCustomerLabel':
        renderStockCustomerLabel(fieldEl, data);
        break;
      case 'warehouses':
        renderWarehouses(fieldEl, data);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn(`Unknown async key: ${key}`);
    }
  });
}
